import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Box, ScrollArea, Text, Group, Paper, ActionIcon, Stack, TextInput, CopyButton, Tooltip } from '@mantine/core';
import { FileText, ChevronRight, ChevronDown, Share2, Link, Eye, EyeOff, Trash2 } from 'lucide-react';
import { Note } from '../../types/notes';
import { NoteRichTextEditor } from '../notes/NoteRichTextEditor';
import { useUpdateNote, useDeleteNote } from '../../hooks/useNotes';
import { useDebouncedCallback } from '@mantine/hooks';
import { NoteEditorProvider } from '../../contexts/NoteEditorContext';
import { FloatingRichTextToolbar } from '../notes/FloatingRichTextToolbar';
import { EditorContextMenu } from '../notes/EditorContextMenu';
import TaskLink from '../notes/extensions/TaskLinkExtension';
import { todoApi } from '../../api/todoApi';
import { notesApi } from '../../api/notesApi';
import { useUser } from '../../contexts/UserContext';
import { ConvertToTaskDialog } from '../notes/ConvertToTaskDialog';
import TaskPreviewDialog from '../notes/TaskPreviewDialog';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '../../contexts/NavigationContext';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Editor } from '@tiptap/react';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';

interface HeaderNode {
  id: string;
  text: string;
  level: number;
  noteId: number;
  element?: Element;
}

interface DocumentNotesModeProps {
  notes: Note[];
  searchQuery: string;
  focusNoteId?: number | null;
  onNoteFocused?: () => void;
}

const extractHeaders = (content: string, noteId: number): HeaderNode[] => {
  if (!content || content.trim() === '') return [];
  
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const headers = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    
    return Array.from(headers).map((header, index) => ({
      id: `note-${noteId}-header-${index}`,
      text: header.textContent?.trim() || 'Untitled Header',
      level: parseInt(header.tagName.charAt(1)),
      noteId,
      element: header
    }));
  } catch (error) {
    console.error('Error parsing HTML content:', error);
    return [];
  }
};

const getDisplayText = (content: string): string => {
  if (!content) return '';
  
  // If it's HTML, extract text content
  if (content.includes('<')) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');
      return doc.body.textContent || '';
    } catch (error) {
      return content;
    }
  }
  
  return content;
};

export const DocumentNotesMode: React.FC<DocumentNotesModeProps> = ({ 
  notes, 
  searchQuery,
  focusNoteId,
  onNoteFocused
}) => {
  const initialContent = notes.length > 0 ? notes[0].content || '' : '';
  const initialTitle = notes.length > 0 ? notes[0].title || '' : '';
  const [selectedNoteId, setSelectedNoteId] = useState<number | null>(
    notes.length > 0 ? notes[0].id : null
  );
  // Track last edited content/title to ensure we can flush saves reliably
  const lastNoteIdRef = useRef<number | null>(selectedNoteId);
  const lastContentRef = useRef<string>(initialContent);
  const lastSavedContentRef = useRef<string>(initialContent);
  const contentDirtyRef = useRef<boolean>(false);
  const lastTitleRef = useRef<string>(initialTitle);
  const lastSavedTitleRef = useRef<string>(initialTitle);
  const titleDirtyRef = useRef<boolean>(false);
  // Collapsed state for headers (notes expand when selected)
  const [collapsedHeaders, setCollapsedHeaders] = useState<Set<string>>(new Set());
  const [editingTitle, setEditingTitle] = useState(false);
  const [localTitle, setLocalTitle] = useState(initialTitle);
  const [editorContent, setEditorContent] = useState(() => initialContent);
  const updateNoteMutation = useUpdateNote();
  const deleteNoteMutation = useDeleteNote();
  const titleInputRef = useRef<HTMLInputElement>(null);
  const isClickingRef = useRef(false);
  const contentEditorRef = useRef<Editor | null>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  // Demo seeding removed
  const { currentUser } = useUser();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const { setCurrentMode } = useNavigation();
  const queryClient = useQueryClient();
  const [menuState, setMenuState] = useState<{ x: number; y: number; visible: boolean }>({ x: 0, y: 0, visible: false });
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [previewState, setPreviewState] = useState<{ open: boolean; taskId: number | null }>({ open: false, taskId: null });
  const [contextTaskId, setContextTaskId] = useState<number | null>(null);
  const selectionHtmlRef = useRef<string>('');
  const selectionTextRef = useRef<string>('');
  // Track created task id if needed later (not used yet)
  // Cache list metadata for legend and chip attrs
  const [listMetaById, setListMetaById] = useState<Map<string, { name: string; color?: string }>>(new Map());

  // Sharing mutations
  const shareNoteMutation = useMutation({
    mutationFn: ({ noteId, userEmail }: { noteId: number; userEmail: string }) =>
      notesApi.shareNote(noteId, userEmail),
    onSuccess: (updatedNote) => {
      console.log('Share note success:', updatedNote);
      queryClient.setQueryData(['notes', currentUser?.email], (oldData: Note[]) =>
        oldData?.map(note => note.id === updatedNote.id ? updatedNote : note) || []
      );
      // Also invalidate queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['notes', currentUser?.email] });
      showSuccess('Note is now publicly shared!');
    },
    onError: (error) => {
      console.error('Share note error:', error);
      showError('Failed to share note');
    }
  });

  const unshareNoteMutation = useMutation({
    mutationFn: ({ noteId, userEmail }: { noteId: number; userEmail: string }) =>
      notesApi.unshareNote(noteId, userEmail),
    onSuccess: (updatedNote) => {
      queryClient.setQueryData(['notes', currentUser?.email], (oldData: Note[]) =>
        oldData?.map(note => note.id === updatedNote.id ? updatedNote : note) || []
      );
      showSuccess('Note is no longer public');
    },
    onError: () => {
      showError('Failed to unshare note');
    }
  });

  const handleShareNote = () => {
    console.log('handleShareNote called', { selectedNote: selectedNote?.id, userEmail: currentUser?.email });
    if (selectedNote && currentUser?.email) {
      shareNoteMutation.mutate({ noteId: selectedNote.id, userEmail: currentUser.email });
    } else {
      console.warn('Cannot share note - missing data:', { selectedNote: !!selectedNote, userEmail: !!currentUser?.email });
    }
  };

  const handleUnshareNote = () => {
    if (selectedNote && currentUser?.email) {
      unshareNoteMutation.mutate({ noteId: selectedNote.id, userEmail: currentUser.email });
    }
  };

  const getPublicUrl = (token: string) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/public/note/${token}`;
  };

  const handleDeleteNote = () => {
    if (selectedNote && window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      const deletedNoteId = selectedNote.id;
      
      // Flush any pending edits for the current note BEFORE deletion
      flushEdits();
      
      deleteNoteMutation.mutate(deletedNoteId, {
        onSuccess: () => {
          // If we deleted the currently selected note, select another one
          const remainingNotes = filteredNotes.filter(note => note.id !== deletedNoteId);
          if (remainingNotes.length > 0) {
            const nextNote = remainingNotes[0];
            
            // Reset all refs to prevent stale data from being saved to the next note
            lastNoteIdRef.current = nextNote.id;
            lastContentRef.current = nextNote.content || '';
            lastSavedContentRef.current = nextNote.content || '';
            contentDirtyRef.current = false;
            lastTitleRef.current = nextNote.title || '';
            lastSavedTitleRef.current = nextNote.title || '';
            titleDirtyRef.current = false;
            
            // Update UI state
            setLocalTitle(nextNote.title || '');
            setEditorContent(nextNote.content || '');
            setSelectedNoteId(nextNote.id);
          } else {
            // No notes left, clear everything
            lastNoteIdRef.current = null;
            lastContentRef.current = '';
            lastSavedContentRef.current = '';
            contentDirtyRef.current = false;
            lastTitleRef.current = '';
            lastSavedTitleRef.current = '';
            titleDirtyRef.current = false;
            setLocalTitle('');
            setEditorContent('');
            setSelectedNoteId(null);
          }
          showSuccess('Note deleted successfully');
        },
        onError: () => {
          showError('Failed to delete note');
        }
      });
    }
  };

  useEffect(() => {
    const loadLists = async () => {
      try {
        if (!currentUser?.email) return;
        const lists = await todoApi.getAllLists(currentUser.email);
        const map = new Map<string, { name: string; color?: string }>();
        for (const l of lists) {
          map.set(String(l.id), { name: l.name, color: l.color });
        }
        setListMetaById(map);
      } catch (e) {
        // ignore
      }
    };
    loadLists();
  }, [currentUser?.email]);

  // Filter notes based on search query with stable sorting
  const filteredNotes = useMemo(() => {
    let filtered = notes;
    
    if (searchQuery.trim()) {
      filtered = notes.filter(note => {
        const title = note.title?.toLowerCase() || '';
        const content = getDisplayText(note.content).toLowerCase();
        const query = searchQuery.toLowerCase();
        
        return title.includes(query) || content.includes(query);
      });
    }
    
    // Ensure stable sorting by creation date (descending - newest first)
    return [...filtered].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [notes, searchQuery]);

  // Build header tree removed; headers are computed inline when rendering

  const selectedNote = filteredNotes.find(note => note.id === selectedNoteId);
  const selectedNoteHeaders = useMemo(
    () =>
      selectedNote
        ? extractHeaders(selectedNote.content || '', selectedNote.id)
        : [],
    [selectedNote?.id, selectedNote?.content]
  );

  // If selected note is not in filtered notes, select the first available note
  useEffect(() => {
    if (!selectedNote && filteredNotes.length > 0) {
      setSelectedNoteId(filteredNotes[0].id);
    }
  }, [selectedNote, filteredNotes]);

  // Handle focusing a specific note when a new note is created
  useEffect(() => {
    if (focusNoteId && filteredNotes.some(note => note.id === focusNoteId)) {
      setSelectedNoteId(focusNoteId);
      
      // Find the new note and set its title
      const newNote = filteredNotes.find(note => note.id === focusNoteId);
      if (newNote) {
        setLocalTitle(newNote.title || '');
        
        // Start editing the title and focus the input field
        setEditingTitle(true);
        setTimeout(() => {
          if (titleInputRef.current) {
            titleInputRef.current.focus();
            titleInputRef.current.select(); // Select all text for easy replacement
          }
        }, 100); // Small delay to ensure the input is rendered
      }
      
      // Notify parent that the focus has been handled
      onNoteFocused?.();
    }
  }, [focusNoteId, filteredNotes, onNoteFocused]);

  const debouncedSaveContent = useDebouncedCallback(
    (noteId: number, content: string) => {
      updateNoteMutation.mutate({ id: noteId, data: { content } });
      lastSavedContentRef.current = content;
      contentDirtyRef.current = false;
    },
    800
  );

  const debouncedSaveTitle = useDebouncedCallback(
    (noteId: number, title: string) => {
      updateNoteMutation.mutate({ id: noteId, data: { title } });
      lastSavedTitleRef.current = title;
      titleDirtyRef.current = false;
    },
    800
  );

  const handleContentChange = (content: string) => {
    if (selectedNote) {
      lastNoteIdRef.current = selectedNote.id;
      lastContentRef.current = content;
      setEditorContent(content);
      if (content !== lastSavedContentRef.current) {
        contentDirtyRef.current = true;
        debouncedSaveContent(selectedNote.id, content);
      }
    }
  };

  const escapeHtml = (str: string) =>
    str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  // Helper: select the task node at mouse event position and return its DOM element + id
  const selectTaskNodeAtPointer = (e: React.MouseEvent | React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const editor = contentEditorRef.current;
    if (!editor || !editor.view) return { el: null as HTMLElement | null, id: null as number | null };
    const target = (e.target as HTMLElement) ?? null;
    const el = target ? (target.closest('.task-chip') as HTMLElement | null) : null;
    if (!el) return { el: null, id: null };
    // Set node selection at coords to ensure visual selection
    const coords = { left: e.clientX, top: e.clientY } as const;
    const posInfo = editor.view.posAtCoords(coords);
    if (posInfo && typeof posInfo.pos === 'number') {
      editor.commands.setNodeSelection(posInfo.pos);
    }
    // Fallback visual selection marker (in case schema fallback HTML is used)
    try {
      const root: HTMLElement = editor.view.dom as HTMLElement;
      root.querySelectorAll('.task-chip.chip-selected').forEach((n) => n.classList.remove('chip-selected'));
      el.classList.add('chip-selected');
    } catch {}
    const idAttr = el.getAttribute('data-task-id');
    const id = idAttr ? Number(idAttr) : null;
    return { el, id };
  };

  // Helper: update a chip's completed attribute by scanning document for id
  const updateChipCompletedById = useCallback((taskId: number, completed: boolean) => {
    const editor = contentEditorRef.current;
    if (!editor || !editor.view || !selectedNote) return;
    let updated = false;
    editor.state.doc.descendants((node: ProseMirrorNode, pos: number) => {
      if (node.type?.name === 'taskLink' && Number(node.attrs?.id) === Number(taskId)) {
        editor.chain().setNodeSelection(pos).updateAttributes('taskLink', { completed }).run();
        updated = true;
        return false;
      }
      return true;
    });
    if (!updated) {
      // Fallback: update DOM attribute for visual consistency if node not found
      const root: HTMLElement | null = editor.view.dom as HTMLElement;
      const el = root?.querySelector(`.task-chip[data-task-id="${taskId}"]`) as HTMLElement | null;
      if (el) el.setAttribute('data-task-completed', completed ? '1' : '0');
    }
  }, [selectedNote]);

  // Helper: clear manual fallback selection class
  const clearManualChipSelection = useCallback(() => {
    const editor = contentEditorRef.current;
    if (!editor || !editor.view || !selectedNote) return;
    try {
      const root: HTMLElement = editor.view.dom as HTMLElement;
      root.querySelectorAll('.task-chip.chip-selected').forEach((n) => n.classList.remove('chip-selected'));
    } catch {}
  }, [selectedNote]);

  // Compute a legend of lists represented by chips in the current editor DOM
  const [legend, setLegend] = useState<Array<{ listId: string; color?: string; name?: string }>>([]);
  const recomputeLegend = useCallback(() => {
    const editor = contentEditorRef.current;
    if (!editor || !editor.view || !selectedNote) return;
    const root: HTMLElement = editor.view.dom as HTMLElement;
    const chips = Array.from(root.querySelectorAll('.task-chip')) as HTMLElement[];
    const map = new Map<string, { listId: string; color?: string; name?: string }>();
    for (const chip of chips) {
      const listId = chip.getAttribute('data-list-id') || 'unknown';
      let name = chip.getAttribute('data-list-name') || undefined;
      // Read inline CSS var for color
      const styleAttr = chip.getAttribute('style') || '';
      const m = /--chip-color:\s*([^;]+)/.exec(styleAttr || '');
      let color = m ? m[1].trim() : undefined;
      // Fallback to cached list meta when missing
      if (!name) name = listMetaById.get(listId)?.name;
      if (!color) color = listMetaById.get(listId)?.color;
      if (!map.has(listId)) {
        map.set(listId, { listId, color, name });
      }
    }
    setLegend(Array.from(map.values()));
  }, [selectedNote, listMetaById]);

  // Recompute legend when content changes or selection updates
  useEffect(() => {
    if (selectedNote) {
      recomputeLegend();
    } else {
      setLegend([]); // Clear legend when no note is selected
    }
  }, [selectedNoteId, selectedNote]);

  useEffect(() => {
    const editor = contentEditorRef.current;
    if (!editor || !selectedNote) return;
    editor.on('update', () => recomputeLegend());
    editor.on('selectionUpdate', () => recomputeLegend());
    return () => {
      if (editor) {
        editor.off('update', () => recomputeLegend());
        editor.off('selectionUpdate', () => recomputeLegend());
      }
    };
  }, [contentEditorRef.current, selectedNote, recomputeLegend]);

  // Helper: update highlight for chips inside a text selection range
  const updateRangeChipHighlights = useCallback(() => {
    const editor = contentEditorRef.current;
    if (!editor || !editor.state || !editor.view || !selectedNote) return;
    // Clear previous range highlights
    try {
      const root: HTMLElement = editor.view.dom as HTMLElement;
      root.querySelectorAll('.task-chip.chip-selected-range').forEach((n) => n.classList.remove('chip-selected-range'));
    } catch {}
    const sel = editor.state.selection;
    if (!sel || sel.empty) return;
    const { from, to } = sel as any;
    try {
      editor.state.doc.nodesBetween(from, to, (node: ProseMirrorNode, pos: number) => {
        if (node?.type?.name === 'taskLink') {
          let el = editor.view.nodeDOM(pos) as HTMLElement | null;
          if (el && !(el as HTMLElement).classList?.contains('task-chip')) {
            el = (el.querySelector && el.querySelector('.task-chip')) as HTMLElement | null;
          }
          if (!el) {
            const id = node.attrs?.id;
            const root: HTMLElement = editor.view.dom as HTMLElement;
            el = root.querySelector(`.task-chip[data-task-id="${id}"]`) as HTMLElement | null;
          }
          if (el) el.classList.add('chip-selected-range');
        }
        return true;
      });
    } catch {}
  }, [selectedNote]);

  // Keep fallback selection visuals in sync with editor selection changes
  useEffect(() => {
    const editor = contentEditorRef.current;
    if (!editor || !selectedNote) return;
    const handleSelectionUpdate = ({ editor }: any) => {
      // If taskLink is not active, remove manual selected class
      if (!editor.isActive('taskLink')) {
        clearManualChipSelection();
      }
      // Update multi-selection highlights for chips within range
      updateRangeChipHighlights();
    };
    editor.on('selectionUpdate', handleSelectionUpdate);
    return () => {
      if (editor) {
        editor.off('selectionUpdate', handleSelectionUpdate);
      }
    };
  }, [contentEditorRef.current, selectedNote, clearManualChipSelection, updateRangeChipHighlights]);

  // Removed dynamic extension push; we pass TaskLink during editor init instead

  const handleTitleChange = (title: string) => {
    setLocalTitle(title);
    if (selectedNote) {
      lastNoteIdRef.current = selectedNote.id;
      lastTitleRef.current = title;
      if (title !== lastSavedTitleRef.current) {
        titleDirtyRef.current = true;
        debouncedSaveTitle(selectedNote.id, title);
      }
    }
  };

  // Update local title when selected note changes (but not when editing)
  useEffect(() => {
    if (selectedNote && !editingTitle) {
      setLocalTitle(selectedNote.title || '');
    }
  }, [selectedNote, editingTitle]);

  useEffect(() => {
    if (!selectedNote) {
      setEditorContent('');
      contentDirtyRef.current = false;
      lastContentRef.current = '';
      lastSavedContentRef.current = '';
      return;
    }

    const nextContent = selectedNote.content || '';

    if (selectedNote.id !== lastNoteIdRef.current) {
      setEditorContent(nextContent);
      lastContentRef.current = nextContent;
      lastSavedContentRef.current = nextContent;
      contentDirtyRef.current = false;
      return;
    }

    if (!contentDirtyRef.current && nextContent !== editorContent) {
      setEditorContent(nextContent);
      lastContentRef.current = nextContent;
      lastSavedContentRef.current = nextContent;
    }
  }, [selectedNote, editorContent]);

  const toggleHeaderCollapse = (headerId: string) => {
    const next = new Set(collapsedHeaders);
    if (next.has(headerId)) next.delete(headerId); else next.add(headerId);
    setCollapsedHeaders(next);
  };

  // Flush any pending edits immediately
  const flushEdits = () => {
    debouncedSaveContent.flush();
    debouncedSaveTitle.flush();
    const id = lastNoteIdRef.current;
    if (!id) return;
    if (contentDirtyRef.current && lastContentRef.current !== lastSavedContentRef.current) {
      updateNoteMutation.mutate({ id, data: { content: lastContentRef.current } });
      lastSavedContentRef.current = lastContentRef.current;
      contentDirtyRef.current = false;
    }
    if (titleDirtyRef.current && lastTitleRef.current !== lastSavedTitleRef.current) {
      updateNoteMutation.mutate({ id, data: { title: lastTitleRef.current } });
      lastSavedTitleRef.current = lastTitleRef.current;
      titleDirtyRef.current = false;
    }
  };

  // When switching selected note, flush the previous note's edits
  useEffect(() => {
    if (lastNoteIdRef.current != null && selectedNoteId !== lastNoteIdRef.current) {
      flushEdits();
    }
    lastNoteIdRef.current = selectedNoteId;
    if (selectedNote) {
      const nextTitle = selectedNote.title || '';
      lastSavedTitleRef.current = nextTitle;
      lastTitleRef.current = nextTitle;
      titleDirtyRef.current = false;
    } else {
      lastSavedTitleRef.current = '';
      lastTitleRef.current = '';
      titleDirtyRef.current = false;
    }
  }, [selectedNoteId, selectedNote]);

  // Flush on page hide/unload and when component unmounts
  useEffect(() => {
    const onBeforeUnload = () => flushEdits();
    const onVisibility = () => { if (document.hidden) flushEdits(); };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibility);
      flushEdits();
    };
  }, []);

  // Note-level collapse removed; selecting a note shows its headings

  const scrollToHeader = (headerId: string) => {
    // Expect id format: note-<noteId>-header-<index>
    try {
      const parts = headerId.split('-');
      const indexStr = parts[parts.length - 1];
      const index = Number.parseInt(indexStr);
      const editor = contentEditorRef.current;
      const container = contentScrollRef.current;
      if (!editor || !editor.view || !container || Number.isNaN(index)) {
        console.warn('Unable to scroll to header:', { hasEditor: !!editor, hasView: !!editor?.view, hasContainer: !!container, index });
        return;
      }

      const editorRoot: HTMLElement = editor.view.dom as HTMLElement;
      const headings = editorRoot.querySelectorAll('h1, h2, h3, h4, h5, h6');
      const target = headings[index] as HTMLElement | undefined;
      if (!target) {
        console.warn('Header index out of range', { index, count: headings.length });
        return;
      }

  // Compute desired scrollTop using DOMRects relative to the scroll container
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const deltaFromTop = targetRect.top - containerRect.top;
  const desiredTop = container.scrollTop + deltaFromTop;
  const padding = 4; // small breathing room
  container.scrollTo({ top: Math.max(0, desiredTop - padding), behavior: 'smooth' });
    } catch (e) {
      console.error('Failed to scroll to header', e);
    }
  };

  if (filteredNotes.length === 0) {
    return (
      <Paper
        shadow="sm"
        p="xl"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          border: '1px solid var(--app-border)',
          backgroundColor: 'var(--app-surface)'
        }}
      >
        <Text size="lg" style={{ color: 'var(--app-text-muted)' }}>
          {searchQuery ? `No notes found matching "${searchQuery}"` : 'No notes available. Create your first note!'}
        </Text>
      </Paper>
    );
  }

  return (
    <NoteEditorProvider>
      <Box style={{ display: 'flex', height: '100%', gap: '16px' }}>
      {/* Left Panel - Tree Navigation */}
      <Paper
        shadow="sm"
        p="md"
        style={{
          width: '300px',
          flexShrink: 0,
          border: '1px solid var(--app-border)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        <Group justify="space-between" mb="md" align="center">
          <Text fw={600} size="sm" style={{ color: 'var(--app-text-primary)' }}>
            My Notes
          </Text>
        </Group>
        
        <ScrollArea
          style={{ flex: 1, minHeight: 0 }}
          scrollbarSize={10}
          offsetScrollbars
        >
          <Box style={{ width: '100%', paddingLeft: '2px' }}>
            <Stack gap="xs" align="stretch">
              {filteredNotes.map((note) => {
                const isSelected = selectedNoteId === note.id;
                const headers = isSelected ? selectedNoteHeaders : [];
                const hasAnyHeaders = headers.length > 0;
                return (
                  <Box key={note.id}>
                    <Group
                      gap="xs"
                      onClick={() => {
                        // Flush current note before switching
                        flushEdits();
                        setSelectedNoteId(note.id);
                      }}
                      style={{
                        padding: '6px 8px',
                        borderRadius: '6px',
                        backgroundColor: isSelected ? 'var(--app-bg)' : 'transparent',
                        cursor: 'pointer'
                      }}
                    >
                      <FileText size={14} />
                      <Text
                        size="sm"
                        style={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {note.title || 'Untitled Note'}
                      </Text>
                    </Group>

                    {hasAnyHeaders && isSelected && (
                      <Box
                        ml="md"
                        mt="xs"
                        style={{
                          borderLeft: `3px solid ${note.color || 'var(--app-border)'}`,
                          backgroundColor: 'var(--app-surface)',
                          borderRadius: '6px',
                          padding: '6px 4px'
                        }}
                      >
                        {(() => {
                          const rendered: JSX.Element[] = [];
                          const collapsedLevelStack: number[] = [];
                          headers.forEach((header, idx) => {
                            while (collapsedLevelStack.length && header.level <= collapsedLevelStack[collapsedLevelStack.length - 1]) {
                              collapsedLevelStack.pop();
                            }
                            const hidden = collapsedLevelStack.length > 0;
                            let hasChildren = false;
                            for (let j = idx + 1; j < headers.length; j++) {
                              const next = headers[j];
                              if (next.level <= header.level) break;
                              if (next.level > header.level) { hasChildren = true; break; }
                            }
                            const isCollapsed = hasChildren && collapsedHeaders.has(header.id);
                            if (isCollapsed) {
                              collapsedLevelStack.push(header.level);
                            }
                            if (hidden) return; // skip hidden descendants

                            rendered.push(
                              <Group
                                key={header.id}
                                gap="xs"
                                style={{
                                  padding: '4px 8px',
                                  paddingLeft: `${(header.level - 1) * 16 + 8}px`,
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                                onClick={() => scrollToHeader(header.id)}
                              >
                                {hasChildren ? (
                                  <ActionIcon
                                    size="xs"
                                    variant="transparent"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={(e) => { e.stopPropagation(); toggleHeaderCollapse(header.id); }}
                                    styles={{
                                      root: {
                                        outline: 'none',
                                        boxShadow: 'none',
                                        '&:focus': { outline: 'none', boxShadow: 'none' },
                                        '&:focus-visible': { outline: 'none', boxShadow: 'none' }
                                      }
                                    }}
                                    title={isCollapsed ? 'Expand' : 'Collapse'}
                                  >
                                    {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                                  </ActionIcon>
                                ) : (
                                  <span style={{ width: 18 }} />
                                )}
                                <Text
                                  size="xs"
                                  style={{
                                    color: header.level <= 2 ? 'var(--app-text-primary)' : 'var(--app-text-secondary)',
                                    fontWeight: header.level <= 2 ? 500 : 400,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {header.text}
                                </Text>
                              </Group>
                            );
                          });
                          return rendered;
                        })()}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </ScrollArea>
      </Paper>
      
      {/* Right Panel - Note Content */}
      <Paper
        shadow="sm"
        p="lg"
        style={{
          flex: 1,
          minHeight: 0,
          border: '1px solid var(--app-border)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
  {selectedNote ? (
          <>
            {/* Note Title with Share Controls */}
            <Box mb="lg">
              <Group justify="space-between" align="flex-start" mb="xs">
                <Box style={{ flex: 1 }}>
                  {editingTitle ? (
                    <TextInput
                      ref={titleInputRef}
                      value={localTitle}
                      onChange={(e) => setLocalTitle(e.currentTarget.value)}
                      onBlur={() => {
                        // Don't blur if we're in the middle of clicking
                        if (isClickingRef.current) {
                          return;
                        }
                        setEditingTitle(false);
                        handleTitleChange(localTitle);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Tab') {
                          e.preventDefault();
                          // Save title and focus content editor
                          setEditingTitle(false);
                          handleTitleChange(localTitle);
                          setTimeout(() => {
                            if (contentEditorRef.current) {
                              contentEditorRef.current.commands.focus();
                            }
                          }, 10);
                        } else if (e.key === 'Enter' || e.key === 'Escape') {
                          setEditingTitle(false);
                          if (e.key === 'Enter') {
                            handleTitleChange(localTitle);
                          }
                        }
                      }}
                      placeholder="Enter note title..."
                      variant="unstyled"
                      size="xl"
                      styles={{
                        input: {
                          fontSize: '28px',
                          fontWeight: 600,
                          color: 'var(--app-text-primary)',
                          padding: 0,
                          border: 'none',
                          backgroundColor: 'transparent'
                        }
                      }}
                    />
                  ) : (
                    <Text 
                      fw={600} 
                      size="xl" 
                      style={{ 
                        color: 'var(--app-text-primary)',
                        cursor: 'pointer'
                      }}
                      onMouseDown={() => {
                        isClickingRef.current = true;
                      }}
                      onClick={() => {
                        setEditingTitle(true);
                        // Focus the input after state update
                        setTimeout(() => {
                          titleInputRef.current?.focus();
                          isClickingRef.current = false;
                        }, 0);
                      }}
                    >
                      {localTitle || 'Untitled Note'}
                    </Text>
                  )}
                </Box>
                
                {/* Subtle Sharing Controls */}
                <Group gap="xs" style={{ flexShrink: 0 }}>
                  {selectedNote.isPublic && selectedNote.publicAccessToken ? (
                    <>
                      <CopyButton value={getPublicUrl(selectedNote.publicAccessToken)}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? 'Copied!' : 'Copy public link'}>
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color={copied ? 'green' : 'blue'}
                              onClick={copy}
                            >
                              <Link size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                      <Tooltip label="Make note private">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="gray"
                          onClick={handleUnshareNote}
                          loading={unshareNoteMutation.isPending}
                        >
                          <EyeOff size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </>
                  ) : (
                    <Tooltip label="Share this note publicly">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="gray"
                        onClick={handleShareNote}
                        loading={shareNoteMutation.isPending}
                      >
                        <Share2 size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  
                  {/* Delete Note Button */}
                  <Tooltip label="Delete this note">
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="red"
                      onClick={handleDeleteNote}
                      loading={deleteNoteMutation.isPending}
                    >
                      <Trash2 size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
              
              <Text size="sm" style={{ color: 'var(--app-text-muted)', display: 'flex', alignItems: 'center' }}>
                Last updated: {new Date(selectedNote.updatedAt).toLocaleDateString()}
                {selectedNote.isPublic && (
                  <span style={{ marginLeft: '8px', display: 'flex', alignItems: 'center' }}>
                    | <Eye size={12} style={{ marginLeft: '8px', marginRight: '4px' }} />
                    Public
                  </span>
                )}
              </Text>
            </Box>
            
            {/* Fixed toolbar (always visible) */}
            <Box ref={toolbarRef} style={{ flexShrink: 0 }}>
              <FloatingRichTextToolbar />
            </Box>

            {/* Rich Text Editor - make content area vertically scrollable, excluding toolbar */}
            <Box
              ref={contentScrollRef}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto' }}
              onBlur={() => flushEdits()}
              onContextMenu={(e) => {
                e.preventDefault();
                const editor = contentEditorRef.current;
                if (!editor) return;
                // Ensure editor has focus so node selection visuals apply
                try { editor.commands.focus(); } catch {}
                const hasSel = editor.state.selection && !editor.state.selection.empty;
                if (hasSel) {
                  selectionHtmlRef.current = editor.getHTML(); // fallback; we’ll use text below
                  selectionTextRef.current = editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to, '\n');
                } else {
                  selectionHtmlRef.current = '';
                  selectionTextRef.current = '';
                }
                // Detect if right-click was on a task chip
                const target = e.target as HTMLElement;
                const chip = target.closest('.task-chip') as HTMLElement | null;
                if (chip) {
                  const idAttr = chip.getAttribute('data-task-id');
                  const id = idAttr ? Number(idAttr) : null;
                  setContextTaskId(id);
                  // Try to select the node at pointer for visual clarity
                  const coords = { left: e.clientX, top: e.clientY } as const;
                  const posInfo = editor.view.posAtCoords(coords);
                  if (posInfo && typeof posInfo.pos === 'number') {
                    editor.commands.setNodeSelection(posInfo.pos);
                  }
                  // Mark visually selected
                  try {
                    const root: HTMLElement = editor.view.dom as HTMLElement;
                    root.querySelectorAll('.task-chip.chip-selected').forEach((n) => n.classList.remove('chip-selected'));
                    chip.classList.add('chip-selected');
                  } catch {}
                } else {
                  setContextTaskId(null);
                  clearManualChipSelection();
                }
                setMenuState({ x: e.clientX, y: e.clientY, visible: true });
              }}
              onClick={(e) => {
                const { id } = selectTaskNodeAtPointer(e);
                if (id != null) {
                  // Stop propagation to avoid editor default placing text cursor
                  e.stopPropagation();
                }
                if (id == null) {
                  // Clicking outside chips should clear fallback selection highlight
                  clearManualChipSelection();
                }
              }}
              onDoubleClick={async (e) => {
                const { id } = selectTaskNodeAtPointer(e);
                if (id != null) {
                  e.stopPropagation();
                  // Refresh completion state before opening preview
                  try {
                    if (currentUser?.email) {
                      const t = await todoApi.getTodo(id, currentUser.email);
                      updateChipCompletedById(id, !!t.isCompleted);
                    }
                  } catch (err) {
                    console.warn('Failed to refresh task before preview', err);
                  }
                  setPreviewState({ open: true, taskId: id });
                }
              }}
            >
              <NoteRichTextEditor
                content={editorContent}
                onChange={handleContentChange}
                placeholder="Start writing your document..."
                isEditing={true}
                autoFocus={false}
                editorRef={contentEditorRef}
                showToolbar={false}
                additionalExtensions={[TaskLink]}
              />
              {/* Task chip styles */}
              <style>
                {`
                  .task-chip {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 2px 8px;
                    border-radius: 999px;
                    background: color-mix(in srgb, var(--chip-color, var(--app-bg)) 40%, var(--app-bg));
                    border: 1px solid var(--chip-color, var(--app-border));
                    box-shadow: inset 2px 0 0 var(--chip-color, transparent);
                    color: var(--app-text-primary);
                    font-size: 12px;
                    line-height: 1.2;
                    user-select: none;
                    cursor: pointer;
                  }
                  .task-chip:hover { background: color-mix(in srgb, var(--chip-color, var(--app-bg)) 48%, var(--app-bg)); }
                  /* Selected state when the node is selected in ProseMirror */
                  .task-chip.ProseMirror-selectednode {
                    background: var(--app-surface);
                    border-color: var(--app-accent, #4c6fff);
                    box-shadow: inset 2px 0 0 var(--chip-color, transparent), 0 0 0 2px color-mix(in srgb, var(--app-accent, #4c6fff) 20%, transparent);
                  }
                  /* Fallback selected state via manual class and range selection */
                  .task-chip.chip-selected,
                  .task-chip.chip-selected-range {
                    background: var(--app-surface);
                    border-color: var(--app-accent, #4c6fff);
                    box-shadow: inset 2px 0 0 var(--chip-color, transparent), 0 0 0 2px color-mix(in srgb, var(--app-accent, #4c6fff) 20%, transparent);
                  }
                  .task-chip::before { content: '☐'; font-size: 12px; opacity: 0.8; }
                  .task-chip[data-task-completed="1"]::before { content: '☑'; }
                `}
              </style>
              <EditorContextMenu
                x={menuState.x}
                y={menuState.y}
                visible={menuState.visible}
                onClose={() => setMenuState((s) => ({ ...s, visible: false }))}
                items={[
                  { label: 'Copy', onClick: () => document.execCommand('copy'), disabled: false },
                  { label: '—', onClick: () => {} },
                  ...(contextTaskId != null ? [
                    { label: 'Open Task', onClick: async () => {
                        // Refresh completion state before opening preview
                        try {
                          if (currentUser?.email && contextTaskId != null) {
                            const t = await todoApi.getTodo(contextTaskId, currentUser.email);
                            updateChipCompletedById(contextTaskId, !!t.isCompleted);
                          }
                        } catch {}
                        setPreviewState({ open: true, taskId: contextTaskId });
                      }, disabled: false },
                  ] : []),
                  // Only show convert option when not right-clicking on an existing task chip
                  ...(contextTaskId == null ? [
                    { label: 'Convert selection to Task', onClick: () => setShowConvertDialog(true), disabled: !contentEditorRef.current || contentEditorRef.current.state.selection.empty }
                  ] : []),
                ]}
              />
              <ConvertToTaskDialog
                isOpen={showConvertDialog}
                noteTitle={(() => {
                  const raw = (selectionTextRef.current || '').trim();
                  const [first] = raw.split(/\r?\n/);
                  return (first || selectedNote.title || 'Untitled Task').trim();
                })()}
                noteContent={(() => {
                  const raw = (selectionTextRef.current || '').trim();
                  const [, ...rest] = raw.split(/\r?\n/);
                  return rest.join('\n').trim();
                })()}
                selectedLines={(() => {
                  const raw = (selectionTextRef.current || '').trim();
                  return raw.split(/\r?\n/).map(s => s.trim());
                })()}
                hideDeleteOption
                onClose={() => setShowConvertDialog(false)}
                onConvert={async (listId: number, _deleteNote: boolean, createMultiple?: boolean, listColor?: string, listNameFromDialog?: string) => {
                  try {
                    if (!currentUser?.email) throw new Error('Not authenticated');
                    const raw = (selectionTextRef.current || '').trim();
                    const lines = raw.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
                    const editor = contentEditorRef.current;
                    const hasNode = editor ? !!(editor.schema?.nodes as any)?.taskLink : false;
                    const listIdStr = String(listId);
                    const listName = listNameFromDialog || listMetaById.get(listIdStr)?.name || '';
                    const resolvedColor = listColor || listMetaById.get(listIdStr)?.color;

                    if (createMultiple && lines.length > 1) {
                      // Create tasks first
                      const createdTasks: Array<{ id: number; title: string; label: string }> = [];
                      for (const line of lines) {
                        const title = line;
                        const description = '';
                        const created = await todoApi.createTodo({ title, description, listId, createdBy: currentUser.email, priority: 'Medium' }, currentUser.email);
                        createdTasks.push({ id: created.id, title, label: line });
                      }
                      // Replace selection with one chip per line on separate paragraphs
                      if (editor) {
                        if (hasNode) {
                          const blocks = createdTasks.map((t) => ({
                            type: 'paragraph',
                            content: [
                              { type: 'taskLink', attrs: { id: t.id, title: t.title, label: t.label, completed: false, listColor: resolvedColor, listId, listName } }
                            ]
                          }));
                          editor.chain().focus().insertContent(blocks as any).run();
                        } else {
                          const html = createdTasks
                            .map((t) => {
                              const safeName = (listName || '').split('"').join('&quot;');
                              return `<p><span data-task-link=\"1\" data-task-id=\"${t.id}\" data-task-completed=\"0\" data-list-id=\"${listId}\" data-list-name=\"${safeName}\" class=\"task-chip\" title=\"${escapeHtml(t.title)}\" style=\"${resolvedColor ? `--chip-color: ${resolvedColor}` : ''}\">${escapeHtml(t.label)}</span></p>`;
                            }) 
                            .join('');
                          editor.chain().focus().insertContent(html).run();
                        }
                      }
                      setShowConvertDialog(false);
                      showSuccess(`${lines.length} tasks created and linked`);
                    } else {
                      const [first, ...rest] = lines;
                      const title = (first || selectedNote.title || 'Untitled Task').trim();
                      const description = (rest || []).join('\n').trim();
                      const created = await todoApi.createTodo({ title, description, listId, createdBy: currentUser.email, priority: 'Medium' }, currentUser.email);
                      if (editor && !editor.state.selection.empty) {
                        // Only show the title (first line) on the pill; do not include description
                        const label = title;
                        if (hasNode) {
                          editor.chain().focus().insertTaskLink({ id: created.id, title, label, completed: false, listColor: resolvedColor, listId, listName }).run();
                        } else {
                          const safeName = (listName || '').split('"').join('&quot;');
                          const html = `<span data-task-link=\"1\" data-task-id=\"${created.id}\" data-task-completed=\"0\" data-list-id=\"${listId}\" data-list-name=\"${safeName}\" class=\"task-chip\" title=\"${escapeHtml(title)}\" style=\"${resolvedColor ? `--chip-color: ${resolvedColor}` : ''}\">${escapeHtml(label)}</span>`;
                          editor.chain().focus().insertContent(html).run();
                        }
                      }
                      setShowConvertDialog(false);
                      showSuccess('Task created and linked');
                    }
                  } catch (err) {
                    console.error(err);
                    showError('Failed to create task');
                  }
                }}
              />
              <TaskPreviewDialog
                isOpen={previewState.open}
                taskId={previewState.taskId}
                onClose={() => setPreviewState({ open: false, taskId: null })}
                onStatusChange={(taskId, completed) => updateChipCompletedById(taskId, completed)}
              />
            </Box>
            {/* Legend pinned at bottom, outside scroll area */}
            {legend.length > 0 && (
              <Box mt="sm" pt="xs" style={{ borderTop: '1px solid var(--app-border)' }}>
                <Group gap="sm" wrap="wrap">
                  {legend.map((l) => (
          <Group
                      key={l.listId}
                      gap="xs"
                      style={{ cursor: /^\d+$/.test(l.listId) ? 'pointer' : 'default' }}
                      onClick={() => {
                        // Navigate to Todos with this list selected
                        if (!/^\d+$/.test(l.listId)) return;
                        try { flushEdits(); } catch {}
                        const listNum = parseInt(l.listId, 10);
            // Explicitly switch mode, then set the list via query param
            try { setCurrentMode('todos'); } catch {}
            // Next tick to allow mode switch to render
            setTimeout(() => navigate(`/?listId=${listNum}`), 0);
                      }}
                      title={l.name ? `View ${l.name} in Todos` : undefined}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          background: l.color || 'var(--app-border)',
                          border: '1px solid var(--app-border)'
                        }}
                        title={l.name || ''}
                      />
                      <Text size="xs" color="var(--app-text-secondary)">{l.name || `List ${l.listId}`}</Text>
                    </Group>
                  ))}
                </Group>
              </Box>
            )}
          </>
        ) : (
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'var(--app-text-muted)'
            }}
          >
            <Text size="lg">Select a note to view and edit</Text>
          </Box>
        )}
      </Paper>
    </Box>
    </NoteEditorProvider>
  );
};

export default DocumentNotesMode;